/**
 * WhatsApp Message Templates page.
 *
 * Admin edits per-document-type message bodies + toggles.
 * Defaults from whatsappTemplates.js; overrides saved to
 * companies/{id}/whatsappTemplates/{key}.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FiSave, FiRotateCcw, FiInfo, FiEye } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import { loadAllTemplates, saveTemplate } from '../../utils/whatsappHelper';
import { TEMPLATE_BY_KEY, renderTemplate } from '../../utils/whatsappTemplates';

const SAMPLE_VARS = {
  customerName: 'ABC Trading',
  vendorName: 'XYZ Supplies',
  staffName: 'Ali Khan',
  companyName: 'Your Company',
  docNumber: 'SI-2026-00012',
  docDate: '2026-05-18',
  totalAmount: 'Rs. 50,000.00',
  balanceAmount: 'Rs. 10,000.00',
  dueDate: '2026-06-01',
  daysOverdue: '7',
  month: 'May',
  year: '2026'
};

const ALL_VARS = Object.keys(SAMPLE_VARS);

const WhatsAppTemplates = () => {
  const { activeCompany } = useAuth();
  const { canEdit } = usePagePermissions('whatsapp', 'templates');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const list = await loadAllTemplates(activeCompany.id);
      setTemplates(list);
      if (!selectedKey && list.length) {
        setSelectedKey(list[0].key);
        setEditing({ ...list[0] });
      }
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally {
      setLoading(false);
    }
  }, [activeCompany?.id, selectedKey]);

  useEffect(() => { load(); }, [load]);

  const selectTemplate = (key) => {
    const t = templates.find(x => x.key === key);
    if (t) { setSelectedKey(key); setEditing({ ...t }); setAlert(null); }
  };

  const handleSave = async () => {
    if (!editing || !canEdit) return;
    setSaving(true);
    try {
      await saveTemplate(activeCompany.id, editing.key, {
        body: editing.body,
        attachPdf: !!editing.attachPdf,
        enabled: editing.enabled !== false
      });
      setAlert({ type: 'success', message: 'Template saved.' });
      await load();
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    if (!editing) return;
    const def = TEMPLATE_BY_KEY[editing.key];
    if (!def) return;
    if (!window.confirm('Reset this template to default content?')) return;
    setEditing({ ...def });
    setAlert({ type: 'info', message: 'Reverted to default. Click Save to persist.' });
  };

  const insertVar = (v) => {
    if (!editing) return;
    setEditing(prev => ({ ...prev, body: (prev.body || '') + `{${v}}` }));
  };

  const preview = useMemo(() => {
    if (!editing) return '';
    return renderTemplate(editing.body, SAMPLE_VARS);
  }, [editing]);

  return (
    <PageLayout pageTitle="WhatsApp Templates">
      <Container>
        <Header>
          <Title>Message Templates</Title>
          <Subtitle>Edit the message body sent for each document type. Use {'{variables}'} for dynamic data.</Subtitle>
        </Header>

        {alert && <Alert $type={alert.type}><FiInfo /> {alert.message}</Alert>}

        {loading ? <Loading>Loading templates…</Loading> : (
          <Layout>
            <List>
              {templates.map(t => (
                <ListItem key={t.key} $active={t.key === selectedKey} onClick={() => selectTemplate(t.key)}>
                  <ListLabel>{t.label}</ListLabel>
                  <ListMeta>
                    {t.enabled === false ? <Off>OFF</Off> : <On>ON</On>}
                    {t.attachPdf && <Pdf>PDF</Pdf>}
                  </ListMeta>
                </ListItem>
              ))}
            </List>

            {editing && (
              <Editor>
                <EditorHead>
                  <EditorTitle>{editing.label}</EditorTitle>
                  <EditorMeta>
                    Recipient: <b>{editing.recipient}</b>
                    {editing.tranType && <> · TranType: <b>{editing.tranType}</b></>}
                  </EditorMeta>
                </EditorHead>

                <FieldRow>
                  <Field>
                    <Label>Enabled</Label>
                    <Toggle>
                      <input type="checkbox" checked={editing.enabled !== false} onChange={e => setEditing({...editing, enabled: e.target.checked})} disabled={!canEdit} />
                      <span>Show Send button on this document</span>
                    </Toggle>
                  </Field>
                  <Field>
                    <Label>Attach PDF</Label>
                    <Toggle>
                      <input type="checkbox" checked={!!editing.attachPdf} onChange={e => setEditing({...editing, attachPdf: e.target.checked})} disabled={!canEdit} />
                      <span>Include the document as a PDF attachment</span>
                    </Toggle>
                  </Field>
                </FieldRow>

                <Field>
                  <Label>Message Body</Label>
                  <Textarea rows={10} value={editing.body || ''} onChange={e => setEditing({...editing, body: e.target.value})} disabled={!canEdit} />
                </Field>

                <Field>
                  <Label>Insert variable</Label>
                  <VarChips>
                    {ALL_VARS.map(v => (
                      <VarChip key={v} onClick={() => insertVar(v)} disabled={!canEdit}>
                        {`{${v}}`}
                      </VarChip>
                    ))}
                  </VarChips>
                </Field>

                <PreviewBox>
                  <PreviewLabel><FiEye /> Preview (with sample data)</PreviewLabel>
                  <PreviewText>{preview}</PreviewText>
                </PreviewBox>

                <Actions>
                  <SaveBtn onClick={handleSave} disabled={!canEdit || saving}>
                    <FiSave /> {saving ? 'Saving…' : 'Save'}
                  </SaveBtn>
                  <ResetBtn onClick={handleReset} disabled={!canEdit || saving}>
                    <FiRotateCcw /> Reset to Default
                  </ResetBtn>
                </Actions>
              </Editor>
            )}
          </Layout>
        )}
      </Container>
    </PageLayout>
  );
};

export default WhatsAppTemplates;

const Container = styled.div`max-width: 1200px; margin: 0 auto; padding: 24px;`;
const Header = styled.div`margin-bottom: 16px;`;
const Title = styled.h1`margin: 0; font-size: 24px; font-weight: 700; color: ${colors.text.primary};`;
const Subtitle = styled.div`color: ${colors.text.secondary}; font-size: 14px; margin-top: 4px;`;
const Alert = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; border-radius: 8px; margin-bottom: 12px; font-size: 14px;
  background: ${({$type}) => $type === 'success' ? '#e8f5e9' : $type === 'error' ? '#fdecea' : '#e7f1ff'};
  color: ${({$type}) => $type === 'success' ? '#1b5e20' : $type === 'error' ? '#b00020' : '#0d47a1'};
`;
const Loading = styled.div`padding: 40px; text-align: center; color: ${colors.text.secondary};`;
const Layout = styled.div`display: grid; grid-template-columns: 280px 1fr; gap: 16px;`;
const List = styled.div`background: #fff; border: 1px solid #eee; border-radius: 8px; overflow: hidden; height: fit-content;`;
const ListItem = styled.div`
  padding: 12px 14px; border-bottom: 1px solid #f0f0f0; cursor: pointer;
  background: ${p => p.$active ? '#fff5f5' : '#fff'};
  border-left: 3px solid ${p => p.$active ? '#800020' : 'transparent'};
  &:hover { background: #fafafa; }
`;
const ListLabel = styled.div`font-weight: 600; font-size: 14px; color: ${colors.text.primary};`;
const ListMeta = styled.div`display: flex; gap: 6px; margin-top: 4px;`;
const On = styled.span`background: #e8f5e9; color: #1b5e20; padding: 1px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;`;
const Off = styled.span`background: #fdecea; color: #b00020; padding: 1px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;`;
const Pdf = styled.span`background: #e7f1ff; color: #0d47a1; padding: 1px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;`;
const Editor = styled.div`background: #fff; border: 1px solid #eee; border-radius: 8px; padding: 20px;`;
const EditorHead = styled.div`margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px solid #eee;`;
const EditorTitle = styled.div`font-size: 18px; font-weight: 700; color: ${colors.text.primary};`;
const EditorMeta = styled.div`font-size: 12px; color: ${colors.text.secondary}; margin-top: 4px;`;
const FieldRow = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 12px;`;
const Field = styled.div`margin-bottom: 14px;`;
const Label = styled.label`display: block; font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; margin-bottom: 6px;`;
const Toggle = styled.label`display: flex; align-items: center; gap: 8px; font-size: 13px; cursor: pointer; input { width: 16px; height: 16px; }`;
const Textarea = styled.textarea`width: 100%; padding: 10px 12px; border: 1px solid #ddd; border-radius: 6px; font-family: inherit; font-size: 13px; resize: vertical;`;
const VarChips = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const VarChip = styled.button`background: #f3eef0; color: #800020; border: 1px solid #e0d0d3; padding: 4px 10px; border-radius: 14px; font-size: 12px; cursor: pointer; &:hover { background: #ebdde0; } &:disabled { opacity: 0.5; cursor: not-allowed; }`;
const PreviewBox = styled.div`background: #f9f9f9; border: 1px dashed #ccc; border-radius: 6px; padding: 12px; margin: 14px 0;`;
const PreviewLabel = styled.div`display: flex; align-items: center; gap: 6px; font-size: 12px; color: ${colors.text.secondary}; margin-bottom: 6px;`;
const PreviewText = styled.div`white-space: pre-wrap; font-size: 13px; line-height: 1.5;`;
const Actions = styled.div`display: flex; gap: 10px; padding-top: 14px; border-top: 1px solid #eee;`;
const SaveBtn = styled.button`display: inline-flex; align-items: center; gap: 8px; background: #800020; color: #fff; border: none; padding: 10px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; &:disabled { opacity: 0.6; cursor: not-allowed; }`;
const ResetBtn = styled.button`display: inline-flex; align-items: center; gap: 8px; background: #fff; color: #800020; border: 1px solid #800020; padding: 10px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; &:disabled { opacity: 0.6; cursor: not-allowed; }`;
